import React, { useState, useEffect } from 'react';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import CustomersService from "../../services/CustomersService";
import BillingShippingService from "../../services/BillingShippingService";
import { useParams, NavLink } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { FaRegEdit } from "react-icons/fa";
import AddressFormComponent from './components/address/AddressFormComponent';
import AddressCardComponent from './components/address/AddressCardComponent';
import EmployeeForm from './components/employee/EmployeeForm';
import EmployeeList from './components/employee/EmployeeList';
import EmployeeService from '../../services/EmployeeService';
import Subscription from './components/premium-support/Subscription';
import SubscriptionTicketService from '../../services/SubscriptionTicketService';
import { ListSubscriptionTicket } from './components/tickets/ListSubscriptionTicket';
import RecentActivity from '../../components/RecentActivity';
import ListOrder from './components/order/ListOrder';

const ViewCustomer = () => {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState('Customer');
    const [customer, setCustomer] = useState({ billingaddresses: [], shippingaddresses: [], employees: [], subscriptions: [], subscriptionTickets: [], products: [] });
    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [error, setError] = useState(null);
    const [tabState, setTabState] = useState({
        billing: { isListVisible: true, isFormVisible: false, editData: {} },
        shipping: { isListVisible: true, isFormVisible: false, editData: {} },
        contact: { isListVisible: true, isFormVisible: false, editData: {} },
        subscription: { isListVisible: true, isFormVisible: false, editDazta: {} },
        subscriptionTicket: { isListVisible: true, isFormVisible: false, editDazta: {} },
        order: { isListVisible: true, isFormVisible: false, editDazta: {} }
    });
    const [planId, setPlanId] = useState(null);
    const [subscriptionId, setSubscriptionId] = useState(null);

    const toggleView = (tab, view, editData = {}) => {
        setTabState((prevState) => ({
            ...prevState,
            [tab]: {
                isListVisible: view === 'card',
                isFormVisible: view === 'form',
                editData
            }
        }))
    }

    const setTab = (type, data, isList, isForm) => {
        if (data?.length < 1) {
            setTabState((prevState) => (
                {
                    ...prevState,
                    [type]: {
                        ...prevState[type],
                        isListVisible: isList,
                        isFormVisible: isForm,
                        editData: prevState[type].editData
                    }
                }
            ))
        }
    }

    useEffect(() => {
        const fetchCustomer = async () => {
            try {
                const response = await CustomersService.get(id);
                if (response.status === 200) {
                    const customerData = response.data.data;
                    setCustomer(customerData);
                    setTab('billing', customerData.billingaddresses, false, true)
                    setTab('shipping', customerData.shippingaddresses, false, true)
                    setTab('contact', customerData.employees, false, true)
                    setTab('subscription', customerData?.subscriptions, true, false)
                    setTab('subscriptionTicket', customerData.subscriptions.flatMap(sub => sub.subscriptionTickets), true, false)
                    setTab('order', customerData?.products, false, true)
                    setSubscriptionId(customerData?.subscriptions[0]?.id);
                    setPlanId(customerData?.subscriptions[0]?.plan?.id);
                } else {
                    setError(response?.data?.message);
                }
            } catch (error) {
                console.log("Some error occured", error);
                setError('Something went wrong');
            } finally {
                setLoadingCustomer(false);
            }
        };

        if (id) {
            fetchCustomer();
        }
    }, [id]);

    const onDelete = async (id, type) => {
        if (!id) return;
        try {
            let updatedData;
            if (type === 'billing') {
                await BillingShippingService.deleteBillingAddress(id);
                updatedData = customer.billingaddresses.filter(address => address.id !== id);
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    billingaddresses: updatedData
                }));
                toast.success("Address deleted successfully!");
                setTab('billing', updatedData, false, true)
            } else if (type === 'shipping') {
                await BillingShippingService.deleteShippingAddress(id);
                updatedData = customer.shippingaddresses.filter(address => address.id !== id);
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    shippingaddresses: updatedData
                })
                );
                toast.success("Address deleted successfully!");
                setTab('shipping', updatedData, false, true)
            } else if (type === 'contact') {
                await EmployeeService.delete(id);
                updatedData = customer.employees.filter(employee => employee.id !== id);
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    employees: updatedData
                })
                );
                toast.success("Contact deleted successfully!");
                setTab('contact', updatedData, false, true)
            } else {
                console.log("Some error");
                toast.error("Some error!");
            }
        } catch (err) {
            console.log("Error in deleting", err);
            toast.error(`Failed to deleting: ${err.message}`);
        }
    };

    const onBillingSubmit = async (data) => {
        try {
            if (tabState.billing.editData.id) {
                const updateBillingAddress = await BillingShippingService.updateBillingAddress({ data });
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    billingaddresses: prevCustomer.billingaddresses.map(address => address.id === data.id ? data : address)
                })
                );
                toggleView('billing', 'card');
                toast.success(updateBillingAddress.data.message);
            } else {
                const createBillingAddress = await BillingShippingService.createBillingAddress({ data });
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    billingaddresses: [...prevCustomer.billingaddresses, createBillingAddress.data.data]
                })
                );
                toggleView('billing', 'card');
                toast.success(createBillingAddress.data.message);
            }
        } catch (error) {
            const message = error.response?.data?.data?.message || 'Error saving billing address!';
            toast.error(message);
            toggleView('billing', 'card');
        }
    };

    const onShippingSubmit = async (data) => {
        try {
            if (tabState.shipping.editData.id) {
                const updateShippingAddress = await BillingShippingService.updateShippingAddress({ data });
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    shippingaddresses: prevCustomer.shippingaddresses.map(address => address.id === data.id ? data : address)
                })
                );
                toggleView('shipping', 'card');
                toast.success(updateShippingAddress.data.message);
            } else {
                const createShippingAddress = await BillingShippingService.createShippingAddress({ data });
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    shippingaddresses: [...prevCustomer.shippingaddresses, createShippingAddress.data.data]
                })
                );
                toggleView('shipping', 'card');
                toast.success(createShippingAddress.data.message);
            }
        } catch (error) {
            const message = error.response?.data?.data?.message || 'Error saving shipping address!';
            toast.error(message);
            toggleView('shipping', 'card');
        }

    };

    const onContactSubmit = async (data) => {
        try {
            if (tabState.contact.editData.id) {
                const updateContact = await EmployeeService.update(tabState.contact.editData.id, { data });
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    employees: prevCustomer.employees.map(employee => employee.id === data.id ? updateContact.data.data : employee)
                })
                );
                toast.success(updateContact.data.message);
                toggleView('contact', 'card');

            } else {
                const createContact = await EmployeeService.create({ data });
                console.log("createContact", createContact)
                setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    employees: [...prevCustomer.employees, createContact.data.data]
                })
                );
                toast.success(createContact.data.message);
                toggleView('contact', 'card');

            }
        } catch (error) {
            if (error.errors) {
                error.errors.forEach((fieldError) => {
                    console.log(fieldError.message.isNotEmpty)
                    setError(fieldError.field, {
                        type: 'manual',
                        message: fieldError.message.isNotEmpty
                    })
                });
            } else {
                console.log("Error in contact data", error);
                const message = error.response?.data?.data?.message || 'Error saving contact!';
                toast.error(message);
            }
        }
    };

    const onTicketSubmit = async (data) => {
        try {
            const response = await SubscriptionTicketService.create({ data });
            setCustomer(prevCustomer => ({
                ...prevCustomer,
                tickets: [...prevCustomer.tickets, response.data.data]
            })
            );
            toast.success(response.data.message);
            toggleView('ticket', 'card');
        } catch (error) {
            if (error.errors) {
                error.errors.forEach((fieldError) => {
                    console.log(fieldError.message.isNotEmpty)
                    setError(fieldError.field, {
                        type: 'manual',
                        message: fieldError.message.isNotEmpty
                    })
                });
            } else {
                console.log("Error in ticket data", error);
                const message = error.response?.data?.data?.message || 'Error saving ticket!';
                toast.error(message);
            }
        }
    }


    const renderCustomer = (customer) => {
        return <div>
            <div className="row">
                <div className="col-12 col-md-12 col-lg-8 mb-4">
                    <div className="card p-4">
                        <p className='d-flex justify-content-between'><strong>Company Name:</strong> {customer.company || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>GST Number:</strong> {customer.gst_no || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Phone:</strong> {customer.phone || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Official Email:</strong> {customer.official_email || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Website:</strong> {customer.website || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Industry Type:</strong> {customer.industry_type || 'N/A'}</p>
                        {customer.industry_type === 'other' && <p className='d-flex justify-content-between'><strong>Specify Other:</strong> {customer.specify_other || 'N/A'}</p>}
                        <p className='d-flex justify-content-between'><strong>Currency:</strong> {customer.currency || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Address:</strong> {customer.address || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>City:</strong> {customer.city || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>State:</strong> {customer.state || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Zipcode:</strong> {customer.zipcode || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Country:</strong> {customer.country || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Employee Count:</strong> {customer.employee_count || 'N/A'}</p>
                        <p className='d-flex justify-content-between'><strong>Status:</strong> {customer.status || 'N/A'}</p>
                    </div>
                </div>
                {/* Recent Activity Section */}
                <div className="col-12 col-md-12 col-lg-4 mb-4">
                    <RecentActivity type={'customer'} />
                </div>
            </div>
        </div>
    }
    const renderContentHeader = (activeTab) => {
        return <header className="header ">
            <div className="header-body">
                <div className="row align-items-end">
                    <div className="col-auto">
                        <ul className="nav nav-tabs header-tabs">
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'Customer' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('Customer')}
                                >
                                    Customer
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'order' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('order')}
                                >
                                    Order
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'billing' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('billing')}
                                >
                                    Billing Address
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'shipping' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('shipping')}
                                >
                                    Shipping Address
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'contact' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('contact')}
                                >
                                    Contacts
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'subscription' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('subscription')}
                                >
                                    Premium Support
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type='button'
                                    className={`nav-link text-center ${activeTab === 'subscriptionTicket' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('subscriptionTicket')}
                                >
                                    Ticket
                                </button>
                            </li>
                        </ul>
                    </div>
                    {activeTab === 'Customer' && (
                        <div>
                            <NavLink className='float-end' to={`/customers/${id}/edit`}>
                                <FaRegEdit size={20} />
                            </NavLink>
                        </div>
                    )}

                </div>
            </div>
        </header>
    }

    const renderAddress = (tab, addresses, onSubmit) => {
        const tabCurrentState = tabState[tab];
        return tabCurrentState.isListVisible && customer[`${tab}addresses`]?.length ? (
            <AddressCardComponent
                company={customer.company}
                addresses={addresses}
                type={tab}
                toggleView={toggleView}
                onDelete={onDelete}
            />
        ) : (
            tabCurrentState.isFormVisible && (
                <AddressFormComponent
                    company={customer.company}
                    customerId={customer.id}
                    editData={tabCurrentState.editData}
                    onSubmit={onSubmit}
                    toggleView={toggleView}
                    tab={tab}
                />
            )
        );
    }

    const renderContact = (tab, employees, onSubmit) => {
        const tabCurrentState = tabState[tab];
        return tabCurrentState.isListVisible && customer[`employees`]?.length ? (
            <EmployeeList
                company={customer.company}
                customerId={customer.id}
                employees={employees}
                type={tab}
                toggleView={toggleView}
                onDelete={onDelete}
            />
        ) : (
            tabCurrentState.isFormVisible && (
                <EmployeeForm
                    company={customer.company}
                    customerId={customer.id}
                    editData={tabCurrentState.editData}
                    onSubmit={onSubmit}
                    toggleView={toggleView}
                    tab={tab}
                />
            )
        )
    }
    const renderSubscription = (tab, subscriptions, onSubmit) => {
        const tabCurrentState = tabState[tab];
        if (tabCurrentState?.isListVisible && customer?.subscriptions) {
            return (
                <Subscription
                    company={customer.company}
                    customerId={customer.id}
                    subscriptions={subscriptions}
                    type={tab}
                    planId={planId}
                    toggleView={toggleView}
                    onDelete={onDelete}
                    setCustomer={setCustomer}
                    setPlanId={setPlanId}
                />
            );
        }
        return null;
    };

    const renderSubscriptionTicket = (tab, subscriptionTickets) => {
        const tabCurrentState = tabState[tab];
        if (tabCurrentState.isListVisible && customer?.subscriptions) {
            return (
                <ListSubscriptionTicket
                    company={customer.company}
                    planId={planId}
                    customerId={customer.id}
                    tickets={subscriptionTickets}
                    subscriptionId={subscriptionId}
                    type={tab}
                    toggleView={toggleView}
                    onDelete={onDelete}
                    setCustomer={setCustomer}
                />
            );
        }
        return null;
    };

    const renderListOrder = (tab, products, onSubmit) => {
        const tabCurrentState = tabState[tab];
        return tabCurrentState.isListVisible && customer[`products`]?.length ? (
            <ListOrder
                company={customer.company}
                customerId={customer.id}
                planId={planId}
                products={products}
                type={tab}
                toggleView={toggleView}
                onDelete={onDelete}
                setCustomer={setCustomer}
            />
        ) : (
            tabCurrentState.isFormVisible && (
                <ListOrder
                    company={customer.company}
                    customerId={customer.id}
                    planId={planId}
                    products={products}
                    editData={tabCurrentState.editData}
                    onSubmit={onSubmit}
                    toggleView={toggleView}
                    tab={tab}
                    setCustomer={setCustomer}
                />
            )
        )
    }
    if (loadingCustomer) return <p>Loading customer...</p>;
    if (error) return <p>Error: {error}</p>;
    return (
        <>
            <ToastContainer />
            <AppSidebar />
            <div className="main-content">
                <AppHeader />
                {/* <div className="container-fluid"> */}
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-12">
                        <div className="container-fluid">
                            <div className=" row">
                                {renderContentHeader(activeTab)}
                                <div className="tab-content">
                                    {activeTab === 'Customer' && (renderCustomer(customer))}
                                    {activeTab === 'billing' && renderAddress('billing', customer.billingaddresses, onBillingSubmit)}
                                    {activeTab === 'shipping' && renderAddress('shipping', customer.shippingaddresses, onShippingSubmit)}
                                    {activeTab === 'contact' && (renderContact('contact', customer.employees, onContactSubmit))}
                                    {activeTab === 'subscription' && (renderSubscription('subscription', customer.subscriptions))}
                                    {activeTab === 'subscriptionTicket' && (renderSubscriptionTicket('subscriptionTicket', customer.subscriptions?.flatMap(sub => sub.subscriptionTickets), onTicketSubmit))}
                                    {activeTab === 'order' && (renderListOrder('order', customer.products))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* </div> */}
            </div>
        </>
    );
}

export default ViewCustomer;
